import * as React from "react";
import { useProminentError } from "../prominent-error-provider";
import { useOneTimePasswordSendCodeForm } from "./one-time-password-send-code-form-provider";

import { useOneTimePasswordSendCode } from "./one-time-password-send-code-provider";
import { useOneTimePasswordUserExperience_asController } from "./one-time-password-user-experience-provider";

interface OneTimePasswordUserExperienceFirstStepSendCodeConsumerInterface {
  isLoading?: boolean;
  handleSendCode: () => void;
}
const defaultValue =
  {} as unknown as OneTimePasswordUserExperienceFirstStepSendCodeConsumerInterface;
const OneTimePasswordUserExperienceFirstStepSendCodeContext =
  React.createContext(defaultValue);

export const ONE_TIME_PASSWORD_SEND_CODE_ERROR_MAX_RETRIES_EXCEEDED_MESSAGE =
  "You have sent too many codes to the email address you specified.";

export const OneTimePasswordUserExperienceFirstStepSendCodeProvider: React.FunctionComponent =
  (props) => {
    const { sendCode } = useOneTimePasswordSendCode();

    const { isSendCodeFormCompleteAndValid, form } =
      useOneTimePasswordSendCodeForm();

    const { handleStartTheVerifyCodeStep } =
      useOneTimePasswordUserExperience_asController();

    const { setProminentError } = useProminentError();

    const [isLoading, setIsLoading] = React.useState(false);

    const handleSendCode: OneTimePasswordUserExperienceFirstStepSendCodeConsumerInterface["handleSendCode"] =
      React.useCallback(async () => {
        const userHasFilledInTheFormCompletely =
          isSendCodeFormCompleteAndValid();
        if (!userHasFilledInTheFormCompletely) {
          return;
        }

        setIsLoading(true);

        try {
          const sendCodeResponse = await sendCode(form.email || "");

          if (!sendCodeResponse?.wasSuccessful) {
            if (
              !!sendCodeResponse?.wasUnsuccessfulDueToUserExceedingMaxRetries
            ) {
              throw new Error(
                ONE_TIME_PASSWORD_SEND_CODE_ERROR_MAX_RETRIES_EXCEEDED_MESSAGE
              );
            } else {
              throw new Error();
            }
          }
        } catch (error) {
          console.error(error);
          setIsLoading(false);

          let errorMessage = "An unknown error has occurred.";
          if (error instanceof Error && error.message) {
            errorMessage = error.message;
          }
          setProminentError(errorMessage);

          return;
        }

        handleStartTheVerifyCodeStep();
        setIsLoading(false);
      }, [
        sendCode,
        handleStartTheVerifyCodeStep,
        isSendCodeFormCompleteAndValid,
        setIsLoading,
        setProminentError,
      ]);

    const value: OneTimePasswordUserExperienceFirstStepSendCodeConsumerInterface =
      React.useMemo(
        () => ({
          isLoading,
          handleSendCode,
        }),
        [isLoading, handleSendCode]
      );
    return (
      <OneTimePasswordUserExperienceFirstStepSendCodeContext.Provider
        value={value}
      >
        {props.children || []}
      </OneTimePasswordUserExperienceFirstStepSendCodeContext.Provider>
    );
  };

export const useOneTimePasswordUserExperienceFirstStepSendCode = () => {
  const context = React.useContext(
    OneTimePasswordUserExperienceFirstStepSendCodeContext
  );
  if (context === defaultValue) {
    throw new Error(
      "useOneTimePasswordUserExperienceFirstStepSendCode must be used within a OneTimePasswordUserExperienceFirstStepSendCodeProvider"
    );
  }
  return context;
};
